import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box, VStack, HStack, Heading, Button, Input, Table, Thead, Tbody, Tr, Th, Td,
  Card, CardHeader, CardBody, Badge, IconButton, useToast, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Text, Progress, Alert, AlertIcon, AlertTitle, AlertDescription, InputGroup, InputLeftElement,
  useColorModeValue, Flex, Skeleton, Stack, Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { DownloadIcon, AttachmentIcon, SearchIcon, ViewIcon } from '@chakra-ui/icons';
import { supabase } from '../../supabaseClient';
import { useOutletContext } from 'react-router-dom';

// Configuration for Test & Panel CSV Upload
const TABLE_CONFIG = {
  name: 'Test Types',
  table: 'auto_split', // Special marker for mixed processing
  requiredFields: [],
  fields: {
    // Combined fields from both tests and panels
    code: { label: 'Test/Panel Code', type: 'text' },
    name: { label: 'Name', type: 'text', required: true },
    description: { label: 'Description/Components', type: 'textarea' },
    category: { label: 'Category', type: 'text' },
    cash_price: { label: 'Cash Price', type: 'number', default: 0 },
    tube_top_color: { label: 'Tube Color', type: 'text' },
    preferred_specimen: { label: 'Preferred Specimen', type: 'text' },
    minimum_volume: { label: 'Minimum Volume', type: 'text' },
    alternative_name: { label: 'Alternative Name', type: 'text' },
    alternative_specimen: { label: 'Alternative Specimen', type: 'text' },
    patient_preparation: { label: 'Patient Preparation', type: 'textarea' },
    interfering_factors: { label: 'Interfering Factors', type: 'textarea' },
    temperature_requirements: { label: 'Temperature Requirements', type: 'text' },
    stability_requirements: { label: 'Stability Requirements', type: 'text' },
    fasting_required: { label: 'Fasting Required', type: 'boolean', default: false },
    methodology: { label: 'Methodology', type: 'text' },
    reference_range: { label: 'Reference Range', type: 'text' },
    clinical_significance: { label: 'Clinical Significance', type: 'textarea' },
    reject_criteria: { label: 'Reject Criteria', type: 'text' },
    cpt_codes: { label: 'CPT Codes', type: 'text' },
    lab_id: { label: 'Lab ID', type: 'text', default: '550e8400-e29b-41d4-a716-446655440000' }
  },
  uniqueFields: ['name', 'lab_id'],
  csvMappings: {
    'TEST CODE': 'code',
    'test code': 'code',
    'CPT CODES': 'cpt_codes',
    'cpt codes': 'cpt_codes',
    'Panel Name ': 'panel_name', // Special field to detect panels (note trailing space)
    'Panel Name': 'panel_name',
    'PANEL NAME': 'panel_name',
    'panel name': 'panel_name',
    'Test Name ': 'test_name', // Special field to detect tests (note trailing space)
    'Test Name': 'test_name',
    'TEST NAME': 'test_name',
    'test name': 'test_name',
    'INCLUDES TESTS': 'description',
    'includes tests': 'description',
    'INCLUDES': 'description',
    'includes': 'description',
    'PATIENT PREPARATION': 'patient_preparation',
    'patient preparation': 'patient_preparation',
    'ALTERNATIVE NAME': 'alternative_name',
    'alternative name': 'alternative_name',
    'Methodology': 'methodology',
    'METHODOLOGY': 'methodology',
    'Reference Range(s)': 'reference_range',
    'REFERENCE RANGE': 'reference_range',
    'PREFERRED SPECIMEN': 'preferred_specimen',
    'preferred specimen': 'preferred_specimen',
    'ALTERNATIVE SPECIMEN': 'alternative_specimen',
    'alternative specimen': 'alternative_specimen',
    'MINIMUM VOLUME': 'minimum_volume',
    'minimum volume': 'minimum_volume',
    'COLLECTION INSTRUCTION': 'interfering_factors',
    'collection instruction': 'interfering_factors',
    'TRANSPORT CONTAINER': 'tube_top_color',
    'transport container': 'tube_top_color',
    'TRANSPORT TEMPERATURE': 'temperature_requirements',
    'transport temperature': 'temperature_requirements',
    'SPECIMEN STABILITY': 'stability_requirements',
    'specimen stability': 'stability_requirements',
    'Clinical Significance ': 'clinical_significance', // Note trailing space
    'Clinical Significance': 'clinical_significance',
    'CLINICAL SIGNIFICANCE': 'clinical_significance',
    'Reject Criteria ': 'reject_criteria', // Note trailing space
    'Reject Criteria': 'reject_criteria',
    'REJECT CRITERIA': 'reject_criteria'
  },
  sampleData: [
    ['TEST CODE', 'Panel Name', 'Test Name', 'INCLUDES TESTS', 'PATIENT PREPARATION', 'ALTERNATIVE NAME', 'PREFERRED SPECIMEN', 'MINIMUM VOLUME'],
    ['10231', 'COMPREHENSIVE METABOLIC PANEL', '', 'Albumin, Glucose, Sodium, Potassium', 'Fasting preferred', 'CMP', '1 mL serum', '0.5 mL'],
    ['330', '', 'Chloride', '', '', '', '1 mL serum', '0.5 mL']
  ]
};

const AdminTestTypesUpload = () => {
  const [panelsData, setPanelsData] = useState([]);
  const [testsData, setTestsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelsSearchQuery, setPanelsSearchQuery] = useState('');
  const [testsSearchQuery, setTestsSearchQuery] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemAssociations, setSelectedItemAssociations] = useState([]);
  const [csvResults, setCsvResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();
  const { adminUser } = useOutletContext();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('md', 'dark-lg');
  const associationBg = useColorModeValue('gray.50', 'gray.700');

  // Paginated fetch to get ALL rows (Supabase caps at 1000 per request)
  const fetchAllRows = async (table, selectQuery = '*', orderCol = 'created_at') => {
    const PAGE = 1000;
    let allData = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table)
        .select(selectQuery)
        .order(orderCol, { ascending: false })
        .range(from, from + PAGE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < PAGE) break; // last page
      from += PAGE;
    }
    return allData;
  };

  // Fetch data for the selected table
  const fetchTableData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all rows from both tables (paginated to bypass 1000-row cap)
      const [testsAll, panelsAll, associationsAll] = await Promise.all([
        fetchAllRows('tests'),
        fetchAllRows('panels'),
        fetchAllRows('panel_tests', 'panel_id, test_id', 'panel_id')
      ]);

      // Build association count maps in O(n) instead of O(n*m)
      const testCountMap = {};
      const panelCountMap = {};
      for (const a of associationsAll) {
        testCountMap[a.test_id] = (testCountMap[a.test_id] || 0) + 1;
        panelCountMap[a.panel_id] = (panelCountMap[a.panel_id] || 0) + 1;
      }

      const enrichedTests = testsAll.map(test => ({
        ...test,
        associationCount: testCountMap[test.id] || 0
      }));

      const enrichedPanels = panelsAll.map(panel => ({
        ...panel,
        associationCount: panelCountMap[panel.id] || 0
      }));

      // Set separate data arrays
      setTestsData(enrichedTests);
      setPanelsData(enrichedPanels);
    } catch (error) {
      console.error('Error fetching table data:', error);
      toast({
        title: 'Error fetching data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  // Robust CSV parser - handles multiline quoted fields and long text
  const parseCSV = (csvText) => {
    console.log('=== Starting ROBUST CSV Parse ===');
    console.log('CSV text length:', csvText.length);
    
    // Parse CSV properly handling multiline quoted fields
    const records = [];
    let currentRecord = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < csvText.length) {
      const char = csvText[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        currentRecord.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // End of record
        currentRecord.push(currentField.trim());
        if (currentRecord.some(field => field.length > 0)) {
          records.push(currentRecord);
        }
        currentRecord = [];
        currentField = '';
        // Skip \r\n combinations
        if (char === '\r' && csvText[i + 1] === '\n') {
          i++;
        }
      } else {
        currentField += char;
      }
      i++;
    }
    
    // Don't forget the last record
    if (currentRecord.length > 0 || currentField.length > 0) {
      currentRecord.push(currentField.trim());
      if (currentRecord.some(field => field.length > 0)) {
        records.push(currentRecord);
      }
    }
    
    console.log('Total records parsed:', records.length);
    
    if (records.length <= 1) {
      console.log('No data records found');
      return [];
    }
    
    // Get headers from first record and track original positions
    const rawHeaders = records[0].map(h => h.replace(/^"|"$/g, '').trim());
    console.log('Raw headers with empty columns:', rawHeaders);
    
    // Create a mapping of non-empty headers to their original positions
    const headerPositions = {};
    const headers = [];
    rawHeaders.forEach((header, index) => {
      if (header && header.trim()) {
        headers.push(header);
        headerPositions[header] = index;
      }
    });
    
    console.log('Clean headers:', headers);
    console.log('Header positions:', headerPositions);
    
    const data = [];
    
    // Process data records (skip header)
    for (let recordIndex = 1; recordIndex < records.length; recordIndex++) {
      const record = records[recordIndex];
      console.log(`Processing record ${recordIndex + 1}:`, record.length + ' fields');
      
      // Skip empty records
      if (!record.some(field => field && field.trim())) {
        console.log(`Skipping empty record ${recordIndex + 1}`);
        continue;
      }
      
             // Create row object with field-specific length limits using correct positions
       const row = {};
       headers.forEach((header) => {
         const originalIndex = headerPositions[header];
         let value = record[originalIndex] || '';
         
         // Apply field-specific length limits based on database schema
         let maxLength = 255; // default
         const lowerHeader = header.toLowerCase();
         
         // VARCHAR(50) fields - be VERY aggressive about catching these
         if (lowerHeader.includes('code') || 
             lowerHeader.includes('tube') ||
             lowerHeader.includes('color') ||
             lowerHeader.includes('cpt') ||
             lowerHeader.includes('methodology') ||
             lowerHeader.includes('transport') ||
             lowerHeader.includes('container') ||
             // Check for any field that might be mapped to a 50-char database column
             lowerHeader.includes('test code') ||
             lowerHeader.includes('panel code') ||
             // Emergency: any field with "short" indicators
             lowerHeader.includes('abbr') ||
             lowerHeader.includes('short') ||
             lowerHeader.includes('code') ||
             // ULTIMATE SAFETY: if the actual database field names we use have these patterns
             header === 'code' ||
             header === 'tube_top_color' ||
             header === 'methodology' ||
             // If this looks like it could be a code-like field, cap it
             (value.length > 30 && (value.match(/^[A-Z0-9\-_\s]+$/i) || value.length < 100))
         ) {
           maxLength = 47; // Even more conservative - leave room for "..."
         } else if (lowerHeader.includes('name') || lowerHeader.includes('alternative_name')) {
           maxLength = 500;
         } else if (lowerHeader.includes('category') || lowerHeader.includes('preferred_specimen') || 
                   lowerHeader.includes('temperature') || lowerHeader.includes('stability') ||
                   lowerHeader.includes('specimen') || lowerHeader.includes('range')) {
           maxLength = 200;
         } else if (lowerHeader.includes('minimum_volume') || lowerHeader.includes('volume')) {
           maxLength = 100;
         }
         
         // CRITICAL SAFETY: If ANY field is too long, apply emergency limits
         if (value.length > 50) {
           // If we haven't specifically allowed it to be longer, cap it at 47
           if (maxLength === 255 && !lowerHeader.includes('name') && !lowerHeader.includes('description') && 
               !lowerHeader.includes('preparation') && !lowerHeader.includes('significance') && 
               !lowerHeader.includes('range') && !lowerHeader.includes('criteria')) {
             console.log(`🚨 SAFETY CAP: "${header}" (pos ${originalIndex}, ${value.length} chars) emergency capped at 47`);
             maxLength = 47;
           }
         }
         
         // Truncate if too long
         if (value.length > maxLength) {
           console.log(`⚠️ TRUNCATING: "${header}" (pos ${originalIndex}, ${value.length} chars) -> ${maxLength} chars`);
           console.log(`   Original: "${value.substring(0, 100)}..."`);
           value = value.substring(0, maxLength - 3) + '...';
         }
         
         // Debug: log all fields to see what's being processed
         if (value.length > 45) {
           console.log(`🔍 Long field detected: "${header}" (pos ${originalIndex}) = ${value.length} chars, limit: ${maxLength}`);
         }
         
         row[header] = value;
       });
      
      // Apply field mappings
      const mappedRow = {};
      Object.keys(row).forEach(key => {
        const mappedKey = TABLE_CONFIG.csvMappings[key] || key;
        mappedRow[mappedKey] = row[key];
      });
      
      // Determine if this is a panel or test
      const panelName = mappedRow.panel_name || mappedRow['Panel Name '] || mappedRow['Panel Name'] || '';
      const testName = mappedRow.test_name || mappedRow['Test Name '] || mappedRow['Test Name'] || '';
      
      console.log(`Record ${recordIndex + 1} - Panel: "${panelName.substring(0, 30)}...", Test: "${testName.substring(0, 30)}..."`);
      
      if (panelName && panelName.trim()) {
        mappedRow.name = panelName.trim();
        mappedRow.record_type = 'panel';
        data.push(mappedRow);
      } else if (testName && testName.trim()) {
        mappedRow.name = testName.trim();
        mappedRow.record_type = 'test';
        data.push(mappedRow);
      } else if (mappedRow.name && mappedRow.name.trim()) {
        // Fallback: accept plain 'name' column as a test
        mappedRow.record_type = 'test';
        data.push(mappedRow);
      } else {
        // Skip rows with no identifiable name
      }
    }

    console.log('=== CSV Parse Complete ===');
    console.log('Total valid rows parsed:', data.length);
    data.forEach((row, index) => {
      console.log(`${index + 1}. ${row.record_type}: ${row.name}`);
    });
    
    return data;
  };

  const checkForDuplicates = async (data) => {
    const config = TABLE_CONFIG;
    const uniqueFields = config.uniqueFields || [];

    if (uniqueFields.length === 0) return { duplicates: [], newRecords: data };

    // Batch fetch all existing names from both tables in 2 queries instead of N*2
    const [testsResult, panelsResult] = await Promise.all([
      supabase.from('tests').select('name'),
      supabase.from('panels').select('name')
    ]);

    const existingNames = new Set();
    (testsResult.data || []).forEach(t => existingNames.add(t.name?.trim().toLowerCase()));
    (panelsResult.data || []).forEach(p => existingNames.add(p.name?.trim().toLowerCase()));

    const duplicates = [];
    const newRecords = [];

    for (const record of data) {
      const name = (record.name || '').trim().toLowerCase();
      if (name && existingNames.has(name)) {
        duplicates.push(record);
      } else {
        newRecords.push(record);
      }
    }

    return { duplicates, newRecords };
  };

  // Automatically create panel-test associations based on included test names
  const createAutomaticTestAssociations = async (panelId, description) => {
    if (!description || !description.trim()) return;

    console.log(`🔗 Creating automatic associations for panel ${panelId}`);
    console.log(`Description: ${description}`);

    try {
      // Parse test names from description (comma-separated list)
      const testNames = description
        .split(',')
        .map(name => name.trim())
        .map(name => name.replace(/\s*\([^)]*\)/g, '')) // Remove anything in parentheses like "(calculated)"
        .filter(name => name.length > 2); // Filter out very short names

      console.log(`🔍 Looking for tests:`, testNames);

      if (testNames.length === 0) return;

      // Find matching tests in database
      const { data: existingTests, error: testError } = await supabase
        .from('tests')
        .select('id, name')
        .in('name', testNames);

      if (testError) {
        console.error('Error fetching tests:', testError);
        return;
      }

      console.log(`✅ Found ${existingTests?.length || 0} matching tests:`, existingTests?.map(t => t.name));

      if (!existingTests || existingTests.length === 0) return;

      // Create associations for found tests
      const associations = existingTests.map(test => ({
        panel_id: panelId,
        test_id: test.id
      }));

      const { error: associationError } = await supabase
        .from('panel_tests')
        .insert(associations);

      if (associationError) {
        console.error('Error creating associations:', associationError);
      } else {
        console.log(`🎉 Created ${associations.length} panel-test associations`);
      }

    } catch (error) {
      console.error('Error in createAutomaticTestAssociations:', error);
    }
  };

  const uploadCSV = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const text = await file.text();
      const data = parseCSV(text);
      
      if (data.length === 0) {
        throw new Error('No valid data found in CSV file');
      }

      console.log('Parsed CSV data:', data);
      console.log('Current config:', TABLE_CONFIG);
      
      // Check for duplicates
      const { duplicates, newRecords } = await checkForDuplicates(data);
      
      const results = {
        total: data.length,
        successful: 0,
        failed: 0,
        duplicates: duplicates.length,
        errors: []
      };

      if (newRecords.length === 0) {
        throw new Error('All records are duplicates or invalid');
      }

      // Transform all records first, then batch insert
      const panelRows = [];
      const testRows = [];
      const panelSourceRows = []; // keep original rows for association creation

      for (let i = 0; i < newRecords.length; i++) {
        const row = newRecords[i];

        const transformedRow = {};

        // Map CSV fields to database fields
        Object.entries(TABLE_CONFIG.fields).forEach(([fieldName, fieldConfig]) => {
          let value = row[fieldName] || row[fieldName.toLowerCase()] || '';

          // Check if there's a CSV mapping for this field
          if (TABLE_CONFIG.csvMappings) {
            for (const [csvColumn, mappedField] of Object.entries(TABLE_CONFIG.csvMappings)) {
              if (mappedField === fieldName && row[csvColumn] !== undefined) {
                value = row[csvColumn];
                break;
              }
            }
          }

          // Type conversion
          if (fieldConfig.type === 'boolean') {
            transformedRow[fieldName] = value === 'true' || value === '1' || value === 'yes' || value === 'TRUE';
          } else if (fieldConfig.type === 'number' && value) {
            transformedRow[fieldName] = parseFloat(value) || fieldConfig.default || 0;
          } else {
            transformedRow[fieldName] = value || fieldConfig.default || '';
          }

          if (!transformedRow[fieldName] && fieldConfig.default !== undefined) {
            transformedRow[fieldName] = fieldConfig.default;
          }
        });

        // Determine the name from available columns
        const rawName = (row.panel_name && row.panel_name.trim()) ||
                        (row.test_name && row.test_name.trim()) ||
                        (row.name && row.name.trim()) || '';

        if (!rawName) {
          results.errors.push(`Row ${i + 1}: No panel name, test name, or name found`);
          results.failed++;
          continue;
        }

        transformedRow.name = rawName;

        // Smart detection: determine if this is a panel or individual test
        // Panels: contain "panel" in name, or are ALL UPPERCASE multi-word names
        const nameLower = rawName.toLowerCase();
        const isPanel = nameLower.includes('panel') ||
                        (rawName === rawName.toUpperCase() && rawName.length > 5 && rawName.includes(' '));

        if (isPanel) {
          panelRows.push(transformedRow);
          panelSourceRows.push(row);
        } else {
          if (!transformedRow.description || transformedRow.description.trim() === '') {
            transformedRow.description = 'Individual laboratory test';
          }
          testRows.push(transformedRow);
        }
      }

      // Apply common processing to all rows
      const applyCommon = (row) => {
        if (!row.cash_price) row.cash_price = 0;
        if (!row.lab_id) row.lab_id = '550e8400-e29b-41d4-a716-446655440000';

        // Set empty code to null so PostgreSQL unique constraint (code, lab_id) ignores it
        if (!row.code || row.code.trim() === '') {
          row.code = null;
        }

        // Truncate fields that exceed varchar limits
        const allowedLongFields = ['name', 'description', 'patient_preparation', 'interfering_factors',
          'clinical_significance', 'alternative_name', 'preferred_specimen',
          'alternative_specimen', 'temperature_requirements', 'stability_requirements',
          'reference_range', 'reject_criteria'];

        Object.keys(row).forEach(key => {
          if (typeof row[key] === 'string' && row[key].length > 50 && !allowedLongFields.includes(key)) {
            row[key] = row[key].substring(0, 47) + '...';
          }
        });

        // Convert remaining empty strings to null to avoid unique constraint issues
        Object.keys(row).forEach(key => {
          if (row[key] === '') row[key] = null;
        });

        return row;
      };

      panelRows.forEach(applyCommon);
      testRows.forEach(applyCommon);

      // Batch insert with individual fallback on failure
      const BATCH_SIZE = 50;
      const totalRows = panelRows.length + testRows.length;
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      // Helper: insert a single row with retry on network failure
      const insertSingle = async (table, row) => {
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const { data: single, error: singleErr } = await supabase
              .from(table)
              .insert([row])
              .select()
              .single();

            if (singleErr) {
              // Retry on network errors
              if (attempt === 0 && (singleErr.message.includes('Failed to fetch') || singleErr.message.includes('NetworkError'))) {
                await delay(500);
                continue;
              }
              return { data: null, error: singleErr };
            }
            return { data: single, error: null };
          } catch (err) {
            if (attempt === 0) {
              await delay(500);
              continue;
            }
            return { data: null, error: err };
          }
        }
        return { data: null, error: new Error('Max retries exceeded') };
      };

      // Helper: insert a batch, fall back to individual inserts if batch fails
      const insertBatch = async (table, batch) => {
        const batchResults = { successful: 0, failed: 0, insertedData: [] };

        // Try batch insert first
        const { data: inserted, error } = await supabase
          .from(table)
          .insert(batch)
          .select();

        if (error) {
          console.log(`Batch insert to ${table} failed (${batch.length} rows):`, error.message);
          if (results.errors.length < 3) {
            console.log('First row in failed batch:', JSON.stringify(batch[0], null, 2));
          }
        }

        if (!error) {
          batchResults.successful = (inserted || []).length;
          batchResults.insertedData = inserted || [];
          return batchResults;
        }

        // Batch failed — fall back to throttled individual inserts
        for (let j = 0; j < batch.length; j++) {
          const { data: single, error: singleErr } = await insertSingle(table, batch[j]);

          if (singleErr) {
            if (singleErr.message?.includes('duplicate key') || singleErr.message?.includes('unique constraint')) {
              // DB-level duplicate — count as duplicate, not failure
              results.duplicates++;
            } else {
              results.errors.push(`${table} "${batch[j].name}": ${singleErr.message}`);
              batchResults.failed++;
            }
          } else {
            batchResults.successful++;
            if (single) batchResults.insertedData.push(single);
          }

          // Throttle: 50ms between individual inserts to prevent rate limiting
          if (j < batch.length - 1) await delay(50);
        }

        return batchResults;
      };

      // Insert panels in batches
      let processedCount = 0;
      for (let i = 0; i < panelRows.length; i += BATCH_SIZE) {
        const batch = panelRows.slice(i, i + BATCH_SIZE);
        const sourceSlice = panelSourceRows.slice(i, i + BATCH_SIZE);
        processedCount += batch.length;
        setUploadProgress((processedCount / totalRows) * 100);

        const batchResult = await insertBatch('panels', batch);
        results.successful += batchResult.successful;
        results.failed += batchResult.failed;

        // Create panel-test associations for successfully inserted panels
        for (let j = 0; j < batchResult.insertedData.length; j++) {
          const panel = batchResult.insertedData[j];
          const sourceRow = sourceSlice[j];
          if (panel && sourceRow?.description) {
            await createAutomaticTestAssociations(panel.id, sourceRow.description);
          }
        }

        // Throttle between batches
        if (i + BATCH_SIZE < panelRows.length) await delay(200);
      }

      // Insert tests in batches
      for (let i = 0; i < testRows.length; i += BATCH_SIZE) {
        const batch = testRows.slice(i, i + BATCH_SIZE);
        processedCount += batch.length;
        setUploadProgress((processedCount / totalRows) * 100);

        const batchResult = await insertBatch('tests', batch);
        results.successful += batchResult.successful;
        results.failed += batchResult.failed;

        // Throttle between batches
        if (i + BATCH_SIZE < testRows.length) await delay(200);
      }

      // Log activity
      try {
        await supabase.from('admin_activity_log').insert([
          {
            action: `CSV Upload ${TABLE_CONFIG.name}`,
            username: adminUser?.name || 'Unknown',
            details: `${results.successful} successful, ${results.failed} failed, ${results.duplicates} duplicates`,
            ip_address: 'admin-panel'
          }
        ]);
      } catch (logError) {
        // Activity log failure is non-critical
      }

      setCsvResults(results);
      await fetchTableData();

      toast({
        title: 'Upload completed',
        description: `${results.successful} records uploaded successfully`,
        status: results.failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = TABLE_CONFIG.sampleData;
    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample_panel_test_split.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportDataCSV = (type) => {
    // type: 'panels', 'tests', or 'all'
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const fields = Object.keys(TABLE_CONFIG.fields);
    const headers = Object.values(TABLE_CONFIG.fields).map(f => f.label);

    let rows = [];
    if (type === 'panels' || type === 'all') {
      panelsData.forEach(item => {
        rows.push(fields.map(f => escapeCSV(item[f])));
      });
    }
    if (type === 'tests' || type === 'all') {
      testsData.forEach(item => {
        rows.push(fields.map(f => escapeCSV(item[f])));
      });
    }

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    a.download = `test_types_backup_${type}_${date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Export Complete',
      description: `Exported ${rows.length} ${type === 'all' ? 'panels & tests' : type} to CSV`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const renderTableHeaders = () => {
    return Object.entries(TABLE_CONFIG.fields).map(([fieldName, fieldConfig]) => (
      <Th key={fieldName}>{fieldConfig.label}</Th>
    ));
  };

  const fieldKeys = useMemo(() => Object.keys(TABLE_CONFIG.fields), []);

  const renderTableRow = useCallback((item) => {
    return fieldKeys.map((fieldName) => (
      <Td key={fieldName}>
        {typeof item[fieldName] === 'boolean' ? (
          <Badge colorScheme={item[fieldName] ? 'green' : 'red'}>
            {item[fieldName] ? 'Yes' : 'No'}
          </Badge>
        ) : fieldName === 'description' ? (
          <Text isTruncated maxWidth="200px" title={item[fieldName] || ''}>
            {item[fieldName]}
          </Text>
        ) : fieldName === 'name' ? (
          <HStack spacing={2}>
            <Text
              color="blue.500"
              cursor="pointer"
              onClick={() => openTestDetail(item)}
              _hover={{ textDecoration: 'underline' }}
              title={`Click to view details`}
            >
              {item[fieldName]}
            </Text>
            {item.associationCount > 0 && (
              <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                {item.associationCount}
              </Badge>
            )}
          </HStack>
        ) : (
          <Text isTruncated maxWidth="150px" title={item[fieldName] || ''}>
            {item[fieldName]}
          </Text>
        )}
      </Td>
    ));
  }, [fieldKeys]);

  // Debounced search values
  const [debouncedPanelsSearch, setDebouncedPanelsSearch] = useState('');
  const [debouncedTestsSearch, setDebouncedTestsSearch] = useState('');
  const panelsTimerRef = useRef(null);
  const testsTimerRef = useRef(null);

  const handlePanelsSearch = useCallback((value) => {
    setPanelsSearchQuery(value);
    if (panelsTimerRef.current) clearTimeout(panelsTimerRef.current);
    panelsTimerRef.current = setTimeout(() => setDebouncedPanelsSearch(value), 300);
  }, []);

  const handleTestsSearch = useCallback((value) => {
    setTestsSearchQuery(value);
    if (testsTimerRef.current) clearTimeout(testsTimerRef.current);
    testsTimerRef.current = setTimeout(() => setDebouncedTestsSearch(value), 300);
  }, []);

  // Check if search term matches as an acronym of a name
  // e.g. "BMP" matches "Basic Metabolic Panel", "CBC" matches "Complete Blood Count"
  const matchesAcronym = (name, search) => {
    if (!name || !search) return false;
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 2) return false;
    const acronym = words.map(w => w[0]).join('').toLowerCase();
    return acronym === search || acronym.startsWith(search);
  };

  // Search helper: checks name substring + acronym match only
  const matchesSearch = (item, searchLower) => {
    if (!item.name) return false;
    if (item.name.toLowerCase().includes(searchLower)) return true;
    if (item.alternative_name && item.alternative_name.toLowerCase().includes(searchLower)) return true;
    return matchesAcronym(item.name, searchLower);
  };

  // Memoized filtered data - only recalculates when data or debounced search changes
  const filteredPanelsData = useMemo(() => {
    if (!debouncedPanelsSearch) return panelsData;
    const searchLower = debouncedPanelsSearch.toLowerCase();
    return panelsData.filter(item => matchesSearch(item, searchLower));
  }, [panelsData, debouncedPanelsSearch]);

  const filteredTestsData = useMemo(() => {
    if (!debouncedTestsSearch) return testsData;
    const searchLower = debouncedTestsSearch.toLowerCase();
    return testsData.filter(item => matchesSearch(item, searchLower));
  }, [testsData, debouncedTestsSearch]);

  // Pagination state
  const [panelsPage, setPanelsPage] = useState(0);
  const [testsPage, setTestsPage] = useState(0);
  const PAGE_SIZE = 50;

  // Reset page when search changes
  useEffect(() => { setPanelsPage(0); }, [debouncedPanelsSearch]);
  useEffect(() => { setTestsPage(0); }, [debouncedTestsSearch]);

  const [testDetailLoading, setTestDetailLoading] = useState(false);

  // Fetch associations for the selected item
  const fetchItemAssociations = async (item) => {
    try {
      let associations = [];
      
      // Check if this is a panel (exists in panelsData) or test (exists in testsData)
      const isPanel = panelsData.some(p => p.id === item.id);
      
      if (isPanel) {
        // For panels, get associated tests
        const { data, error } = await supabase
          .from('panel_tests')
          .select(`
            test_id,
            tests (
              id,
              name,
              code,
              description
            )
          `)
          .eq('panel_id', item.id);
          
        if (error) throw error;
        associations = data?.map(pt => ({ ...pt.tests, relationship: 'contains' })) || [];
      } else {
        // For tests, get associated panels
        const { data, error } = await supabase
          .from('panel_tests')
          .select(`
            panel_id,
            panels (
              id,
              name,
              code,
              description
            )
          `)
          .eq('test_id', item.id);
          
        if (error) throw error;
        associations = data?.map(pt => ({ ...pt.panels, relationship: 'included_in' })) || [];
      }
      
      setSelectedItemAssociations(associations);
      console.log(`Found ${associations.length} associations for ${item.name}`);
      
    } catch (error) {
      console.error('Error fetching associations:', error);
      setSelectedItemAssociations([]);
    }
  };

  const openTestDetail = async (item) => {
    setTestDetailLoading(true);
    try {
      // Set the selected item and open modal
      setSelectedItem(item);
      setSelectedItemAssociations([]); // Reset associations
      onDetailOpen();
      
      // Fetch associations
      await fetchItemAssociations(item);
      
      // Log the click
      await supabase.from('admin_activity_log').insert([
        {
          action: 'View Test/Panel Detail',
          username: adminUser?.name || 'Unknown',
          details: `Viewed details for: ${item.name}`,
          ip_address: 'admin-panel'
        }
      ]);
      
      console.log('Opening detail for:', item);
      
    } catch (error) {
      console.error('Error logging detail view:', error);
    } finally {
      setTestDetailLoading(false);
    }
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Heading size="xl">Test Types Management</Heading>
          <HStack flexWrap="wrap">
            <Button
              leftIcon={<DownloadIcon />}
              variant="outline"
              colorScheme="green"
              onClick={() => exportDataCSV('all')}
              isDisabled={panelsData.length === 0 && testsData.length === 0}
            >
              Export All
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              variant="outline"
              colorScheme="purple"
              size="sm"
              onClick={() => exportDataCSV('panels')}
              isDisabled={panelsData.length === 0}
            >
              Export Panels
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              variant="outline"
              colorScheme="teal"
              size="sm"
              onClick={() => exportDataCSV('tests')}
              isDisabled={testsData.length === 0}
            >
              Export Tests
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              variant="outline"
              onClick={downloadSampleCSV}
            >
              Sample CSV
            </Button>
            <Button
              leftIcon={<AttachmentIcon />}
              colorScheme="blue"
              onClick={onOpen}
            >
              Upload CSV
            </Button>
          </HStack>
        </Flex>

        {/* Data Tables with Tabs */}
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardHeader>
            <Heading size="md">Current {TABLE_CONFIG.name} Records</Heading>
          </CardHeader>
          <CardBody>
            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                <Tab>
                  <Flex align="center" gap={2}>
                    <Text>Panels</Text>
                    <Badge colorScheme="purple" borderRadius="full">
                      {filteredPanelsData.length}
                    </Badge>
                  </Flex>
                </Tab>
                <Tab>
                  <Flex align="center" gap={2}>
                    <Text>Tests</Text>
                    <Badge colorScheme="green" borderRadius="full">
                      {filteredTestsData.length}
                    </Badge>
                  </Flex>
                </Tab>
              </TabList>
              
              <TabPanels>
                {/* Panels Tab */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    {/* Search for Panels */}
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search panels..."
                        value={panelsSearchQuery}
                        onChange={(e) => handlePanelsSearch(e.target.value)}
                      />
                    </InputGroup>

                    {/* Panels Table */}
                    {loading ? (
                      <Stack>
                        <Skeleton height="20px" />
                        <Skeleton height="20px" />
                        <Skeleton height="20px" />
                      </Stack>
                    ) : (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {renderTableHeaders()}
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filteredPanelsData.slice(panelsPage * PAGE_SIZE, (panelsPage + 1) * PAGE_SIZE).map((item, index) => (
                              <Tr key={item.id || index}>
                                {renderTableRow(item)}
                                <Td>
                                  <IconButton
                                    icon={<ViewIcon />}
                                    size="sm"
                                    onClick={() => openTestDetail(item)}
                                    isLoading={testDetailLoading}
                                    aria-label="View panel details"
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>

                        <Flex mt={3} justify="space-between" align="center">
                          <Text fontSize="sm" color="gray.500">
                            Showing {Math.min(panelsPage * PAGE_SIZE + 1, filteredPanelsData.length)}-{Math.min((panelsPage + 1) * PAGE_SIZE, filteredPanelsData.length)} of {filteredPanelsData.length} panels
                          </Text>
                          <HStack>
                            <Button size="sm" onClick={() => setPanelsPage(p => p - 1)} isDisabled={panelsPage === 0}>Prev</Button>
                            <Text fontSize="sm">Page {panelsPage + 1} of {Math.max(1, Math.ceil(filteredPanelsData.length / PAGE_SIZE))}</Text>
                            <Button size="sm" onClick={() => setPanelsPage(p => p + 1)} isDisabled={(panelsPage + 1) * PAGE_SIZE >= filteredPanelsData.length}>Next</Button>
                          </HStack>
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* Tests Tab */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    {/* Search for Tests */}
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search tests..."
                        value={testsSearchQuery}
                        onChange={(e) => handleTestsSearch(e.target.value)}
                      />
                    </InputGroup>

                    {/* Tests Table */}
                    {loading ? (
                      <Stack>
                        <Skeleton height="20px" />
                        <Skeleton height="20px" />
                        <Skeleton height="20px" />
                      </Stack>
                    ) : (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {renderTableHeaders()}
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filteredTestsData.slice(testsPage * PAGE_SIZE, (testsPage + 1) * PAGE_SIZE).map((item, index) => (
                              <Tr key={item.id || index}>
                                {renderTableRow(item)}
                                <Td>
                                  <IconButton
                                    icon={<ViewIcon />}
                                    size="sm"
                                    onClick={() => openTestDetail(item)}
                                    isLoading={testDetailLoading}
                                    aria-label="View test details"
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>

                        <Flex mt={3} justify="space-between" align="center">
                          <Text fontSize="sm" color="gray.500">
                            Showing {Math.min(testsPage * PAGE_SIZE + 1, filteredTestsData.length)}-{Math.min((testsPage + 1) * PAGE_SIZE, filteredTestsData.length)} of {filteredTestsData.length} tests
                          </Text>
                          <HStack>
                            <Button size="sm" onClick={() => setTestsPage(p => p - 1)} isDisabled={testsPage === 0}>Prev</Button>
                            <Text fontSize="sm">Page {testsPage + 1} of {Math.max(1, Math.ceil(filteredTestsData.length / PAGE_SIZE))}</Text>
                            <Button size="sm" onClick={() => setTestsPage(p => p + 1)} isDisabled={(testsPage + 1) * PAGE_SIZE >= filteredTestsData.length}>Next</Button>
                          </HStack>
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>

        {/* Upload Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Upload {TABLE_CONFIG.name} CSV</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box p={4} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="md">
                  <Text fontWeight="bold">CSV Format Requirements:</Text>
                  <Text fontSize="sm">
                    Required columns: {TABLE_CONFIG.requiredFields.join(', ')}<br/>
                    Duplicate checking on: {TABLE_CONFIG.uniqueFields.join(', ') || 'None'}<br/>
                    All fields: {Object.keys(TABLE_CONFIG.fields).join(', ')}
                  </Text>
                </Box>

                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      uploadCSV(file);
                    }
                  }}
                  disabled={isUploading}
                />

                {isUploading && (
                  <Box>
                    <Text mb={2}>Uploading...</Text>
                    <Progress value={uploadProgress} colorScheme="blue" />
                  </Box>
                )}

                {csvResults && (
                  <Alert status={csvResults.failed > 0 ? 'warning' : 'success'}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Upload Results</AlertTitle>
                      <AlertDescription>
                        Total: {csvResults.total}, 
                        Successful: {csvResults.successful}, 
                        Failed: {csvResults.failed}, 
                        Duplicates: {csvResults.duplicates}
                        {csvResults.errors.length > 0 && (
                          <Box mt={2}>
                            <Text fontWeight="bold">Errors:</Text>
                            {csvResults.errors.slice(0, 10).map((error, index) => (
                              <Text key={index} fontSize="sm">{error}</Text>
                            ))}
                            {csvResults.errors.length > 10 && (
                              <Text fontSize="sm">... and {csvResults.errors.length - 10} more</Text>
                            )}
                          </Box>
                        )}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Detail Modal */}
        <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Flex align="center" gap={3}>
                <Badge colorScheme={selectedItem?.id && panelsData.find(p => p.id === selectedItem.id) ? 'purple' : 'green'}>
                  {selectedItem?.id && panelsData.find(p => p.id === selectedItem.id) ? 'Panel' : 'Test'}
                </Badge>
                <Text>{selectedItem?.name}</Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedItem && (
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="bold" mb={2}>Basic Information</Text>
                    <VStack spacing={2} align="stretch">
                      {selectedItem.code && (
                        <HStack>
                          <Text fontWeight="semibold" minW="150px">Code:</Text>
                          <Text>{selectedItem.code}</Text>
                        </HStack>
                      )}
                      <HStack>
                        <Text fontWeight="semibold" minW="150px">Name:</Text>
                        <Text>{selectedItem.name}</Text>
                      </HStack>
                      {selectedItem.description && (
                        <HStack align="start">
                          <Text fontWeight="semibold" minW="150px">Description:</Text>
                          <Text>{selectedItem.description}</Text>
                        </HStack>
                      )}
                      {selectedItem.category && (
                        <HStack>
                          <Text fontWeight="semibold" minW="150px">Category:</Text>
                          <Text>{selectedItem.category}</Text>
                        </HStack>
                      )}
                      {selectedItem.alternative_name && (
                        <HStack>
                          <Text fontWeight="semibold" minW="150px">Alternative Name:</Text>
                          <Text>{selectedItem.alternative_name}</Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>

                  {(selectedItem.patient_preparation || selectedItem.fasting_required) && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Patient Preparation</Text>
                      <VStack spacing={2} align="stretch">
                        {selectedItem.fasting_required && (
                          <HStack>
                            <Text fontWeight="semibold" minW="150px">Fasting Required:</Text>
                            <Badge colorScheme="orange">Yes</Badge>
                          </HStack>
                        )}
                        {selectedItem.patient_preparation && (
                          <HStack align="start">
                            <Text fontWeight="semibold" minW="150px">Instructions:</Text>
                            <Text>{selectedItem.patient_preparation}</Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {(selectedItem.preferred_specimen || selectedItem.minimum_volume || selectedItem.tube_top_color) && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Specimen Requirements</Text>
                      <VStack spacing={2} align="stretch">
                        {selectedItem.preferred_specimen && (
                          <HStack>
                            <Text fontWeight="semibold" minW="150px">Preferred Specimen:</Text>
                            <Text>{selectedItem.preferred_specimen}</Text>
                          </HStack>
                        )}
                        {selectedItem.minimum_volume && (
                          <HStack>
                            <Text fontWeight="semibold" minW="150px">Minimum Volume:</Text>
                            <Text>{selectedItem.minimum_volume}</Text>
                          </HStack>
                        )}
                        {selectedItem.tube_top_color && (
                          <HStack>
                            <Text fontWeight="semibold" minW="150px">Tube Color:</Text>
                            <Text>{selectedItem.tube_top_color}</Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {selectedItem.cash_price && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Pricing</Text>
                      <HStack>
                        <Text fontWeight="semibold" minW="150px">Cash Price:</Text>
                        <Text>${selectedItem.cash_price}</Text>
                      </HStack>
                    </Box>
                  )}

                  {/* Panel/Test Associations */}
                  {selectedItemAssociations.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>
                        {selectedItemAssociations[0]?.relationship === 'contains' ? 'Contains Tests' : 'Included in Panels'}
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {selectedItemAssociations.map((association, index) => (
                          <HStack key={association.id || index} p={2} bg={associationBg} borderRadius="md">
                            <Badge colorScheme={association.relationship === 'contains' ? 'green' : 'purple'}>
                              {association.relationship === 'contains' ? 'Test' : 'Panel'}
                            </Badge>
                            <VStack align="start" spacing={0} flex={1}>
                              <Text fontWeight="semibold">{association.name}</Text>
                              {association.code && (
                                <Text fontSize="sm" color="gray.500">Code: {association.code}</Text>
                              )}
                              {association.description && (
                                <Text fontSize="sm" color="gray.600" isTruncated maxWidth="300px">
                                  {association.description}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  <Box>
                    <Text fontWeight="bold" mb={2}>Metadata</Text>
                    <VStack spacing={2} align="stretch">
                      <HStack>
                        <Text fontWeight="semibold" minW="150px">Created:</Text>
                        <Text>{new Date(selectedItem.created_at).toLocaleString()}</Text>
                      </HStack>
                      {selectedItem.updated_at && (
                        <HStack>
                          <Text fontWeight="semibold" minW="150px">Updated:</Text>
                          <Text>{new Date(selectedItem.updated_at).toLocaleString()}</Text>
                        </HStack>
                      )}
                      <HStack>
                        <Text fontWeight="semibold" minW="150px">ID:</Text>
                        <Text fontSize="sm" fontFamily="mono">{selectedItem.id}</Text>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onDetailClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default AdminTestTypesUpload; 